import {
  extractPositionedTextFromPdfBlob,
  extractTextFromPdfFile,
  extractTextFromPdfViaOcr,
  PdfPositionedTextItem,
} from 'src/app/shared/helpers/document-pdf.util';
import {
  blobLooksLikeExcel,
  excelTableToPrintApi,
  parseHeatPlanExcelWorkbook,
} from './heat-plan-graph-excel.util';

const PORT_ORDER = [
  '1-PORT',
  '1-STBD',
  '2-PORT',
  '2-STBD',
  '3-PORT',
  '3-STBD',
  '4-PORT',
  '4-STBD',
  '5-PORT',
  '5-STBD',
  '6-PORT',
  '6-STBD',
  'SLOP-PORT',
  'SLOP-STBD',
];

const MAX_CHART_PORTS = 8;
const LAST_N_DAYS = 7;

const MONTH_NAMES =
  'jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december';

const DATE_IN_TEXT = new RegExp(
  `(\\d{1,2})\\s*[\\/\\-\\.]\\s*(\\d{1,2})\\s*[\\/\\-\\.]\\s*(\\d{4})|` +
    `(\\d{4})\\s*[\\/\\-\\.]\\s*(\\d{1,2})\\s*[\\/\\-\\.]\\s*(\\d{1,2})|` +
    `(\\d{1,2})\\s*[-/]?\\s*(${MONTH_NAMES})\\s*[-/]?\\s*(\\d{4})`,
  'i',
);

function pad2(n: string | number): string {
  return String(n).padStart(2, '0');
}

const MONTH_MAP: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3,
  apr: 4, april: 4, may: 5, jun: 6, june: 6, jul: 7, july: 7,
  aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};

function formatUkDate(day: number, month: number, year: number): string {
  return `${pad2(day)}/${pad2(month)}/${year}`;
}

function parseCellAsDate(str: string): string | null {
  const s = str.trim();
  if (!s) {
    return null;
  }

  const monthMatch = s.match(
    new RegExp(`^(\\d{1,2})\\s*[-/]?\\s*(${MONTH_NAMES})\\s*[-/]?\\s*(\\d{4})$`, 'i'),
  );
  if (monthMatch) {
    const mo = MONTH_MAP[monthMatch[2].toLowerCase().slice(0, 3)] ??
      MONTH_MAP[monthMatch[2].toLowerCase()];
    if (mo) {
      return formatUkDate(Number(monthMatch[1]), mo, Number(monthMatch[3]));
    }
  }

  const compact = s.replace(/\s+/g, '');
  const slash = compact.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (slash) {
    return formatUkDate(Number(slash[1]), Number(slash[2]), Number(slash[3]));
  }

  const spaced = s.match(DATE_IN_TEXT);
  if (spaced) {
    if (spaced[4]) {
      return formatUkDate(Number(spaced[6]), Number(spaced[5]), Number(spaced[4]));
    }
    if (spaced[7]) {
      const mo =
        MONTH_MAP[spaced[8].toLowerCase().slice(0, 3)] ??
        MONTH_MAP[spaced[8].toLowerCase()];
      if (mo) {
        return formatUkDate(Number(spaced[7]), mo, Number(spaced[9]));
      }
    }
    return formatUkDate(Number(spaced[1]), Number(spaced[2]), Number(spaced[3]));
  }

  const serial = parseFloat(s.replace(/,/g, ''));
  if (Number.isFinite(serial) && serial > 30000 && serial < 70000) {
    const d = new Date((serial - 25569) * 86400000);
    if (!Number.isNaN(d.getTime())) {
      return formatUkDate(d.getDate(), d.getMonth() + 1, d.getFullYear());
    }
  }

  return null;
}

function parseUkDateToMs(dateStr: string): number {
  const m = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) {
    return NaN;
  }
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
}

function extractDateFromRowText(text: string): string | null {
  const normalized = text.replace(/\s+/g, ' ').trim();
  const m = normalized.match(DATE_IN_TEXT);
  if (m) {
    if (m[4]) {
      return formatUkDate(Number(m[6]), Number(m[5]), Number(m[4]));
    }
    if (m[7]) {
      const mo =
        MONTH_MAP[m[8].toLowerCase().slice(0, 3)] ?? MONTH_MAP[m[8].toLowerCase()];
      if (mo) {
        return formatUkDate(Number(m[7]), mo, Number(m[9]));
      }
    }
    return formatUkDate(Number(m[1]), Number(m[2]), Number(m[3]));
  }
  return parseCellAsDate(normalized.replace(/\s/g, ''));
}

function extractDateFromRowGroup(group: PdfPositionedTextItem[]): string | null {
  for (const cell of group) {
    const d = parseCellAsDate(cell.str);
    if (d) {
      return d;
    }
  }
  return extractDateFromRowText(group.map((g) => g.str).join(' '));
}

function toTemp(n: number): boolean {
  return Number.isFinite(n) && n >= 5 && n <= 120;
}

/** Typical cargo heating temps — excludes date fragments (day/month). */
function isHeatingTemp(n: number): boolean {
  return Number.isFinite(n) && n >= 15 && n <= 120;
}

function isLikelyDatePart(n: number, dateStr: string): boolean {
  const m = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) {
    return false;
  }
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const rounded = Math.round(n);
  return (
    rounded === day ||
    rounded === month ||
    rounded === year ||
    rounded === year % 100
  );
}

function filterHeatingNumbers(nums: number[], dateStr: string | null): number[] {
  return nums.filter(
    (n) =>
      isHeatingTemp(n) && (!dateStr || !isLikelyDatePart(n, dateStr)),
  );
}

function parseTempCell(str: string): number | null {
  const s = str.trim();
  if (!s || /^x$/i.test(s) || /^on$/i.test(s)) {
    return null;
  }
  if (/°c|°f|temp\.?$/i.test(s) && !/^-?\d/.test(s)) {
    return null;
  }
  const n = parseFloat(s.replace(/,/g, ''));
  return toTemp(n) ? n : null;
}

async function blobLooksLikePdf(blob: Blob): Promise<boolean> {
  if (blob.type.includes('pdf')) {
    return true;
  }
  const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  return (
    head.length >= 4 &&
    head[0] === 0x25 &&
    head[1] === 0x50 &&
    head[2] === 0x44 &&
    head[3] === 0x46
  );
}

function normalizePrintJson(
  json: Record<string, unknown>,
  row: { ship_name?: string; ship_imo?: string | number },
): Record<string, unknown> {
  let table = json['response'];
  if (typeof table === 'string') {
    table = JSON.parse(table as string);
  }
  if (table && typeof table === 'object') {
    const t = table as { columns?: string[]; data?: unknown[][] };
    if (t.columns?.length && t.data?.length) {
      return {
        ...json,
        vessel_name: json['vessel_name'] ?? row.ship_name,
        imo: json['imo'] ?? row.ship_imo,
        response: table,
      };
    }
  }
  if (json['columns'] && json['data']) {
    return {
      vessel_name: json['vessel_name'] ?? row.ship_name,
      imo: json['imo'] ?? row.ship_imo,
      response: {
        columns: json['columns'],
        data: json['data'],
        index: json['index'],
      },
    };
  }
  throw new Error('JSON response has no heat plan table');
}

/** Heat Plan rows: PORT temp, heat, STBD temp, heat per tank — keep PORT temps only. */
function extractPortTempsForChart(nums: number[], dateStr: string | null): number[] {
  const heating = filterHeatingNumbers(nums, dateStr);
  if (!heating.length) {
    return [];
  }

  if (heating.length >= 20) {
    const ports: number[] = [];
    for (let i = 0; i < MAX_CHART_PORTS; i++) {
      const idx = i * 4;
      if (idx < nums.length && isHeatingTemp(nums[idx])) {
        ports.push(nums[idx]);
      }
    }
    if (ports.length >= 3) {
      return ports;
    }
  }

  if (heating.length <= MAX_CHART_PORTS) {
    return heating;
  }

  const ports: number[] = [];
  for (let i = 0; i < MAX_CHART_PORTS; i++) {
    const idx = i * 4;
    if (idx < nums.length && isHeatingTemp(nums[idx])) {
      ports.push(nums[idx]);
    }
  }
  return ports.length >= 3 ? ports : heating.slice(0, MAX_CHART_PORTS);
}

function inferChartPortLabels(portCount: number): string[] {
  const n = Math.min(Math.max(portCount, 1), MAX_CHART_PORTS);
  return Array.from({ length: n }, (_, i) => `${i + 1}-PORT`);
}

function dedupeDateRows(
  dates: string[],
  tempRows: number[][],
): { dates: string[]; tempRows: number[][] } {
  const byDate = new Map<string, number[]>();
  dates.forEach((d, i) => byDate.set(d, tempRows[i]));
  const sorted = [...byDate.entries()].sort(
    (a, b) => parseUkDateToMs(a[0]) - parseUkDateToMs(b[0]),
  );
  return {
    dates: sorted.map(([d]) => d),
    tempRows: sorted.map(([, row]) => row),
  };
}

function takeLastNDays(
  dates: string[],
  tempRows: number[][],
  n: number,
): { dates: string[]; tempRows: number[][] } {
  const start = Math.max(0, dates.length - n);
  return {
    dates: dates.slice(start),
    tempRows: tempRows.slice(start),
  };
}

function buildTablePayload(
  dates: string[],
  tempRows: number[][],
  row: { ship_name?: string; ship_imo?: string | number },
): Record<string, unknown> {
  const deduped = dedupeDateRows(dates, tempRows);
  const last7 = takeLastNDays(deduped.dates, deduped.tempRows, LAST_N_DAYS);
  dates = last7.dates;
  tempRows = last7.tempRows;

  const portCount = Math.max(
    ...tempRows.map((r) => extractPortTempsForChart(r, null).length),
    6,
  );
  const portLabels = inferChartPortLabels(portCount);
  const columns = portLabels.map((t) => `${t} (°C)`);
  columns.push('Weighted Avg Temp');

  const index = dates.map((d) => parseUkDateToMs(d));
  const data = tempRows.map((nums, rowIdx) => {
    const ports = extractPortTempsForChart(nums, dates[rowIdx]);
    while (ports.length < portLabels.length) {
      ports.push(0);
    }
    const weighted =
      nums.find((n) => isHeatingTemp(n) && n > 50) ??
      (ports.filter((v) => v > 0).length > 0
        ? ports.filter((v) => v > 0).reduce((a, b) => a + b, 0) /
          ports.filter((v) => v > 0).length
        : 0);
    return [...ports.slice(0, portLabels.length), Number(weighted).toFixed(1)];
  });

  return {
    vessel_name: row.ship_name,
    imo: row.ship_imo,
    response: { columns, index, data },
  };
}

function groupPdfItemsIntoRows(
  items: PdfPositionedTextItem[],
  yTolerance: number,
): PdfPositionedTextItem[][] {
  if (!items.length) {
    return [];
  }

  const sorted = [...items].sort((a, b) => b.y - a.y);
  const rows: PdfPositionedTextItem[][] = [];

  for (const item of sorted) {
    const existing = rows.find((r) => Math.abs(r[0].y - item.y) <= yTolerance);
    if (existing) {
      existing.push(item);
    } else {
      rows.push([item]);
    }
  }

  rows.forEach((r) => r.sort((a, b) => a.x - b.x));
  return rows;
}

function rowGroupsToText(rowGroups: PdfPositionedTextItem[][]): string {
  return rowGroups
    .map((g) => g.map((c) => c.str).join(' '))
    .filter(Boolean)
    .join('\n');
}

function extractTempsFromGroup(
  group: PdfPositionedTextItem[],
  dateStr: string | null,
): number[] {
  const nums: number[] = [];
  for (const cell of group) {
    if (parseCellAsDate(cell.str)) {
      continue;
    }
    const t = parseTempCell(cell.str);
    if (t !== null && isHeatingTemp(t)) {
      nums.push(t);
    }
  }
  return nums;
}

function parseRowGroups(
  rowGroups: PdfPositionedTextItem[][],
): { dates: string[]; tempRows: number[][] } | null {
  const byHeader = parseByDateHeader(rowGroups);
  if (byHeader) {
    return byHeader;
  }
  const byDominant = parseByDominantDateColumn(rowGroups);
  if (byDominant) {
    return byDominant;
  }
  return parseByRowScan(rowGroups);
}

function parseByDateHeader(
  rowGroups: PdfPositionedTextItem[][],
): { dates: string[]; tempRows: number[][] } | null {
  let headerIdx = -1;

  for (let i = 0; i < rowGroups.length; i++) {
    if (rowGroups[i].some((c) => /^date$/i.test(c.str.trim()))) {
      headerIdx = i;
      break;
    }
  }

  if (headerIdx < 0) {
    return null;
  }

  const dates: string[] = [];
  const tempRows: number[][] = [];

  for (let i = headerIdx + 1; i < rowGroups.length; i++) {
    const group = rowGroups[i];
    const line = group.map((g) => g.str).join(' ').toLowerCase();
    if (
      /load temp|pour|voy range|disch range|heating plan period|boiler consp/i.test(
        line,
      )
    ) {
      break;
    }

    const dateStr = extractDateFromRowGroup(group);
    if (!dateStr) {
      continue;
    }

    if (/heating plan period/i.test(line) && !/\d+\.\d/.test(line)) {
      continue;
    }

    const nums = extractTempsFromGroup(group, dateStr);
    const ports = extractPortTempsForChart(nums, dateStr);
    if (ports.length >= 1) {
      dates.push(dateStr);
      tempRows.push(ports);
    }
  }

  return dates.length ? { dates, tempRows } : null;
}

/** Detect date column by clustering X positions of date-like cells. */
function parseByDominantDateColumn(
  rowGroups: PdfPositionedTextItem[][],
): { dates: string[]; tempRows: number[][] } | null {
  const dated: { x: number; date: string }[] = [];

  for (const group of rowGroups) {
    for (const cell of group) {
      const d = parseCellAsDate(cell.str);
      if (d) {
        dated.push({ x: cell.x, date: d });
      }
    }
  }

  if (dated.length < 2) {
    return null;
  }

  const buckets = new Map<number, { x: number; count: number }>();
  for (const d of dated) {
    const key = Math.round(d.x / 12) * 12;
    const b = buckets.get(key) ?? { x: 0, count: 0 };
    b.x = (b.x * b.count + d.x) / (b.count + 1);
    b.count += 1;
    buckets.set(key, b);
  }

  const best = [...buckets.values()].sort((a, b) => b.count - a.count)[0];
  const dateColX = best?.x ?? dated[0].x;

  const dates: string[] = [];
  const tempRows: number[][] = [];

  for (const group of rowGroups) {
    const dateCell = group.find(
      (c) => parseCellAsDate(c.str) && Math.abs(c.x - dateColX) < 18,
    );
    const dateStr = dateCell
      ? parseCellAsDate(dateCell.str)!
      : extractDateFromRowGroup(group);

    if (!dateStr) {
      continue;
    }

    const nums = extractTempsFromGroup(group, dateStr);
    const ports = extractPortTempsForChart(nums, dateStr);
    if (ports.length >= 1) {
      dates.push(dateStr);
      tempRows.push(ports);
    }
  }

  const deduped = dedupeDateRows(dates, tempRows);
  return deduped.dates.length >= 2 ? deduped : null;
}

function parseByRowScan(
  rowGroups: PdfPositionedTextItem[][],
): { dates: string[]; tempRows: number[][] } | null {
  const dates: string[] = [];
  const tempRows: number[][] = [];

  for (const group of rowGroups) {
    const dateStr = extractDateFromRowGroup(group);
    if (!dateStr) {
      continue;
    }
    const line = group.map((g) => g.str).join(' ').toLowerCase();
    if (/heating plan period/i.test(line) && !/\d+\.\d/.test(line)) {
      continue;
    }

    const nums = extractTempsFromGroup(group, dateStr);
    const ports = extractPortTempsForChart(nums, dateStr);
    if (ports.length >= 1) {
      dates.push(dateStr);
      tempRows.push(ports);
    }
  }

  return dates.length ? dedupeDateRows(dates, tempRows) : null;
}

function tryParseFromRowGroups(
  items: PdfPositionedTextItem[],
  row: { ship_name?: string; ship_imo?: string | number },
): Record<string, unknown> | null {
  const tolerances = [3, 5, 7, 9, 12, 15];
  let best: { dates: string[]; tempRows: number[][] } | null = null;

  for (const tol of tolerances) {
    const parsed = parseRowGroups(groupPdfItemsIntoRows(items, tol));
    if (parsed && parsed.dates.length > (best?.dates.length ?? 0)) {
      best = parsed;
    }
  }

  if (best?.dates.length) {
    return buildTablePayload(best.dates, best.tempRows, row);
  }
  return null;
}

/** Parse plain text lines. */
export function parseHeatPlanPdfText(
  text: string,
  row: { ship_name?: string; ship_imo?: string | number },
): Record<string, unknown> {
  let lines = text
    .split(/\n/)
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  if (lines.length <= 3) {
    const split = text.split(
      /(?=\d{1,2}\s*[\/\-\.]\s*\d{1,2}\s*[\/\-\.]\s*\d{4})|(?=\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4})/i,
    );
    lines = [...lines, ...split.map((s) => s.trim()).filter(Boolean)];
  }

  const dates: string[] = [];
  const tempRows: number[][] = [];

  for (const line of lines) {
    const dateStr = extractDateFromRowText(line);
    if (!dateStr) {
      continue;
    }

    const afterDate = line.replace(DATE_IN_TEXT, ' ');
    let nums = [...afterDate.matchAll(/-?\d+\.?\d*/g)]
      .map((m) => parseFloat(m[0]))
      .filter((n) => isHeatingTemp(n));

    if (!nums.length) {
      nums = filterHeatingNumbers(
        [...line.matchAll(/-?\d+\.?\d*/g)].map((m) => parseFloat(m[0])),
        dateStr,
      );
    }

    const ports = extractPortTempsForChart(nums, dateStr);
    if (ports.length >= 1) {
      dates.push(dateStr);
      tempRows.push(ports);
    }
  }

  if (!dates.length) {
    const re = /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const dateStr = formatUkDate(Number(m[1]), Number(m[2]), Number(m[3]));
      const slice = text.slice(m.index, m.index + 500);
      const nums = filterHeatingNumbers(
        [...slice.matchAll(/-?\d+\.?\d*/g)].map((x) => parseFloat(x[0])),
        dateStr,
      );
      const ports = extractPortTempsForChart(nums, dateStr);
      if (ports.length >= 1) {
        dates.push(dateStr);
        tempRows.push(ports);
      }
    }
  }

  if (!dates.length) {
    throw new Error('Could not read dates from Heat Plan PDF');
  }

  const deduped = dedupeDateRows(dates, tempRows);
  return buildTablePayload(deduped.dates, deduped.tempRows, row);
}

async function parseHeatPlanPdfBlob(
  blob: Blob,
  row: { ship_name?: string; ship_imo?: string | number },
): Promise<Record<string, unknown>> {
  const file = new File([blob], 'heat-plan.pdf', {
    type: blob.type || 'application/pdf',
  });

  const positioned = await extractPositionedTextFromPdfBlob(blob);
  if (positioned.length) {
    const fromRows = tryParseFromRowGroups(positioned, row);
    if (fromRows) {
      return fromRows;
    }

    const rowText = rowGroupsToText(groupPdfItemsIntoRows(positioned, 6));
    try {
      return parseHeatPlanPdfText(rowText, row);
    } catch {
      /* continue */
    }
  }

  let pdfText = await extractTextFromPdfFile(file);
  const attempts: string[] = [pdfText];

  if (!pdfText.trim()) {
    pdfText = await extractTextFromPdfViaOcr(file);
    attempts.push(pdfText);
  }

  for (const text of attempts) {
    if (!text.trim()) {
      continue;
    }
    try {
      return parseHeatPlanPdfText(text, row);
    } catch {
      /* next */
    }
  }

  const ocrText = await extractTextFromPdfViaOcr(file);
  if (ocrText.trim() && !attempts.includes(ocrText)) {
    return parseHeatPlanPdfText(ocrText, row);
  }

  throw new Error('Could not read dates from Heat Plan PDF');
}

export async function parseHeatPlanPrintBlob(
  blob: Blob,
  row: { ship_name?: string; ship_imo?: string | number },
): Promise<Record<string, unknown>> {
  if (!blob?.size) {
    throw new Error('Empty Heat Plan file from server');
  }

  if (await blobLooksLikeExcel(blob)) {
    try {
      const table = parseHeatPlanExcelWorkbook(await blob.arrayBuffer());
      return excelTableToPrintApi(table, row);
    } catch (excelErr) {
      const msg = excelErr instanceof Error ? excelErr.message : String(excelErr);
      throw new Error(msg);
    }
  }

  const asPdf = await blobLooksLikePdf(blob);

  if (!asPdf) {
    const text = await blob.text();
    const trimmed = text.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return normalizePrintJson(JSON.parse(trimmed), row);
    }
    if (trimmed.toLowerCase().includes('not found')) {
      throw new Error('Heat Plan record not found');
    }
  }

  return parseHeatPlanPdfBlob(blob, row);
}

export function buildHeatPlanPrintPayload(row: {
  ship_imo?: string | number;
  voyage_no?: string | number;
  voyage_month?: string;
  hp_np?: string | number;
  hp_number?: string | number;
}): Record<string, unknown> {
  return {
    report_type: 'heat_plan',
    vessel_imo: row.ship_imo,
    hp_number: row.hp_np ?? row.hp_number,
    voyage_no: row.voyage_no ?? row.voyage_month,
  };
}
